import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe } from '../services/meService';
import { Me, Permission } from '../types/Auth';
import { isAuthenticated } from '../utils/auth';

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  error: boolean;
  hasPermission: (perm: Permission) => boolean;
  hasAnyPermission: (perms: Permission[]) => boolean;
  isRegionScoped: boolean;
  assignedRegions: string[];
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Loads the current staff member's effective permissions once (via /admin/me)
 * and exposes permission checks app-wide. SUPER_ADMIN implicitly passes every
 * check. UI gating is a convenience mirror of backend enforcement, never the
 * security boundary.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authed = isAuthenticated();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    enabled: authed,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const value = useMemo<AuthContextValue>(() => {
    const me = data ?? null;
    const permSet = new Set<Permission>(me?.permissions ?? []);
    const isSuper = me?.role === 'SUPER_ADMIN';

    const hasPermission = (perm: Permission) => isSuper || permSet.has(perm);
    const hasAnyPermission = (perms: Permission[]) =>
      isSuper || perms.some((p) => permSet.has(p));

    return {
      me,
      loading: authed && isLoading,
      error: isError,
      hasPermission,
      hasAnyPermission,
      isRegionScoped: me?.isRegionScoped ?? false,
      assignedRegions: me?.assignedRegions ?? [],
      refresh: () => refetch(),
    };
  }, [data, isLoading, isError, authed, refetch]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** The auth context when there is one (null outside the provider) — for widgets that only refine what they show. */
export const useMaybeAuth = (): AuthContextValue | null => useContext(AuthContext);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

/**
 * The signed-in staff member's own id, or null when there is no provider (component tests that
 * render a page in isolation). Use this for "is this row me?" checks — a missing provider must
 * degrade to "unknown", never throw, since the backend is the real guard either way.
 */
export const useCurrentStaffId = (): number | null => useContext(AuthContext)?.me?.id ?? null;
